import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Card, CardHeader, EmptyState } from '../../components/ui';

const BLANK = {
    observation: '',
    risk_impact: '',
    action: '',
    owner_name: '',
    target_date: '',
    status: 'open',
};

/**
 * "Gaps & Actions" grid. Rows are real records rather than free text, so the
 * hub can chase them across every form — that is the whole point of the
 * follow-up register.
 */
const ActionsEditor = ({ actions = [], onChange, disabled = false, users = [], bare = false }) => {
    const update = (index, patch) =>
        onChange(actions.map((a, i) => (i === index ? { ...a, ...patch } : a)));

    const add = () => onChange([...actions, { ...BLANK, _key: Date.now() }]);
    const remove = (index) => onChange(actions.filter((_, i) => i !== index));

    const body = (
        <>
            <CardHeader
                title="Gaps & Actions"
                subtitle="Anything requiring follow-up. Leave empty when no gap was identified."
                actions={
                    !disabled && (
                        <Button size="sm" icon={Plus} onClick={add}>
                            Add row
                        </Button>
                    )
                }
            />

            {actions.length === 0 ? (
                <EmptyState
                    compact
                    title="No gaps recorded"
                    description="Add a row for each observation that needs an owner and a target date."
                    action={
                        !disabled && (
                            <Button size="sm" icon={Plus} onClick={add}>
                                Add row
                            </Button>
                        )
                    }
                />
            ) : (
                <div className="overflow-x-auto">
                    <table className="table table-compact">
                        <thead>
                            <tr>
                                <th scope="col" style={{ minWidth: 190 }}>Gap / observation</th>
                                <th scope="col" style={{ minWidth: 150 }}>Risk / impact</th>
                                <th scope="col" style={{ minWidth: 190 }}>Action</th>
                                <th scope="col" style={{ minWidth: 130 }}>Owner</th>
                                <th scope="col" style={{ minWidth: 128 }}>Target date</th>
                                <th scope="col" style={{ minWidth: 112 }}>Status</th>
                                {!disabled && <th scope="col" className="w-10" />}
                            </tr>
                        </thead>
                        <tbody>
                            {actions.map((row, i) => (
                                <tr key={row.id || row._key || i} className="group">
                                    <td>
                                        <input
                                            className="input"
                                            aria-label="Gap or observation"
                                            value={row.observation || ''}
                                            onChange={(e) => update(i, { observation: e.target.value })}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            className="input"
                                            aria-label="Risk or impact"
                                            value={row.risk_impact || ''}
                                            onChange={(e) => update(i, { risk_impact: e.target.value })}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            className="input"
                                            aria-label="Action"
                                            value={row.action || ''}
                                            onChange={(e) => update(i, { action: e.target.value })}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="input"
                                            aria-label="Owner"
                                            value={row.owner_name || ''}
                                            onChange={(e) => update(i, { owner_name: e.target.value })}
                                            disabled={disabled}
                                        >
                                            <option value="">Unassigned</option>
                                            {users.map((u) => {
                                                const name = `${u.first_name} ${u.last_name}`;
                                                return <option key={u.id} value={name}>{name}</option>;
                                            })}
                                            {/* Keep a name that is no longer an active user. */}
                                            {row.owner_name &&
                                                !users.some((u) => `${u.first_name} ${u.last_name}` === row.owner_name) && (
                                                    <option value={row.owner_name}>{row.owner_name}</option>
                                                )}
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="date"
                                            className="input"
                                            aria-label="Target date"
                                            value={row.target_date ? String(row.target_date).split('T')[0] : ''}
                                            onChange={(e) => update(i, { target_date: e.target.value })}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            className="input"
                                            aria-label="Status"
                                            value={row.status || 'open'}
                                            onChange={(e) => update(i, { status: e.target.value })}
                                            disabled={disabled}
                                        >
                                            <option value="open">Open</option>
                                            <option value="in_progress">In progress</option>
                                            <option value="closed">Closed</option>
                                        </select>
                                    </td>
                                    {!disabled && (
                                        <td className="text-right">
                                            <button
                                                type="button"
                                                onClick={() => remove(i)}
                                                aria-label="Remove row"
                                                className="p-1 rounded text-ink-400 hover:text-danger hover:bg-danger-subtle transition-colors duration-150 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                            >
                                                <Trash2 size={14} aria-hidden="true" />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    return bare ? <div className="-mx-5">{body}</div> : <Card>{body}</Card>;
};

export default ActionsEditor;
